import React from 'react';
import { NavLink } from 'react-router-dom';

const ICONS = {
  users: (
    <>
      <circle cx="9" cy="7" r="4" />
      <path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      <path d="M21 21v-2a4 4 0 0 0-3-3.87" />
    </>
  ),
  plug: (
    <>
      <path d="M9 2v6" />
      <path d="M15 2v6" />
      <path d="M5 8h14v5a7 7 0 0 1-14 0V8z" />
      <path d="M12 20v2" />
    </>
  ),
  calendar: (
    <>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4" />
      <path d="M8 2v4" />
      <path d="M3 10h18" />
    </>
  ),
  mail: (
    <>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-10 5L2 7" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  shield: <path d="M12 2 4 6v6c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V6l-8-4z" />,
  lock: (
    <>
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </>
  ),
};

const NAV_GROUPS = [
  {
    group: 'Workspace',
    items: [
      { key: 'team', label: 'Team', icon: 'users', to: '/settings/team' },
      { key: 'integrations', label: 'Integrations', icon: 'plug', to: '/settings/integrations' },
    ],
  },
  {
    group: 'Communications',
    items: [
      { key: 'email-schedule', label: 'Email Schedule', icon: 'calendar', to: '/settings/email-schedule' },
      { key: 'email-templates', label: 'Email Templates', icon: 'mail', to: '/settings/email-templates' },
    ],
  },
  {
    group: 'Records',
    items: [
      { key: 'part-time-pay', label: 'Part-Time Pay', icon: 'users', to: '/settings/part-time-pay' },
      { key: 'activity', label: 'Activity Log', icon: 'clock', to: '/settings/activity' },
      { key: 'audit', label: 'Audit Log', icon: 'shield', to: '/settings/audit' },
    ],
  },
  {
    group: 'Account',
    items: [{ key: 'security', label: 'Security', icon: 'lock', to: '/settings/security' }],
  },
];

function NavIcon({ name, active }) {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke={active ? '#9345d6' : 'currentColor'}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={active ? '' : 'text-gray-400 dark:text-gray-500'}
    >
      {ICONS[name]}
    </svg>
  );
}

/**
 * Left-hand navigation for the Settings shell. Groups the consolidated admin
 * sub-pages and highlights the current one.
 *
 * @param {Object} props
 * @param {string} [props.active] - Key of the active item (e.g. 'team'). Falls
 *   back to matching the current route when omitted.
 * @returns {JSX.Element}
 */
export default function SettingsNav({ active }) {
  return (
    <aside className="w-full md:w-[260px] shrink-0 bg-white dark:bg-gray-800 md:border-r border-b md:border-b-0 border-gray-200 dark:border-gray-700 py-6 px-3">
      <p className="text-[11.5px] uppercase tracking-[0.14em] font-bold text-gray-400 dark:text-gray-500 px-3 mb-3">
        Settings
      </p>
      {NAV_GROUPS.map((g) => (
        <div key={g.group} className="mb-5">
          <p className="px-3 mb-1 text-[11px] uppercase tracking-wider text-gray-400 dark:text-gray-500 font-semibold">
            {g.group}
          </p>
          <ul className="space-y-0.5">
            {g.items.map((item) => {
              const isActive = active != null ? item.key === active : undefined;
              const cls = ({ isActive: routeActive }) => {
                const on = isActive != null ? isActive : routeActive;
                return `flex items-center gap-2.5 px-3 py-1.5 rounded-md text-[13.5px] font-medium transition-colors ${
                  on
                    ? 'bg-brand-50 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`;
              };
              return (
                <li key={item.key}>
                  <NavLink to={item.to} className={cls} end>
                    {({ isActive: routeActive }) => {
                      const on = isActive != null ? isActive : routeActive;
                      return (
                        <>
                          <NavIcon name={item.icon} active={on} />
                          {item.label}
                        </>
                      );
                    }}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </aside>
  );
}
